import React, { useState } from 'react';
import { formatDublinDate } from '../utils/date';
import { useAuth } from '../hooks/useAuth';
import { useLocationContext } from '../hooks/useLocationContext';
import {
  useGetOrganizationsQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
} from '../store/api/localApi';
import {
  BuildingLibraryIcon,
  PhoneIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  PlusIcon,
  PencilIcon,
  MapPinIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

interface OrganizationData {
  id: string;
  name: string;
  adminEmail: string;
  billingEmail: string;
  isActive: boolean;
  subscriptionStatus: string;
  subscriptionPlan?: string;
  website?: string;
  phone?: string;
  description?: string;
  locationCount?: number;
  createdAt: string;
  updatedAt: string;
}

const Organizations: React.FC = () => {
  const [selectedOrg, setSelectedOrg] = useState<string | null>(null);
  const [showAddOrgModal, setShowAddOrgModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState<OrganizationData | null>(null);
  const { isSuperAdmin } = useAuth();
  const { enterOrg } = useLocationContext();

  const { data: organizations, isLoading } = useGetOrganizationsQuery();

  const orgs = organizations || [];
  const selectedOrgData = orgs.find((o) => o.id === selectedOrg);

  if (isLoading) {
    return (
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const statusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800';
      case 'TRIAL':
        return 'bg-blue-100 text-blue-800';
      case 'SUSPENDED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="md:flex md:items-center md:justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
              Organizations
            </h2>
            <p className="mt-1 text-sm text-gray-500">Manage all organizations on the platform</p>
          </div>
          {isSuperAdmin && (
            <div className="mt-4 flex md:mt-0 md:ml-4">
              <button
                type="button"
                data-testid="add-org-btn"
                onClick={() => setShowAddOrgModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                Add Organization
              </button>
            </div>
          )}
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {orgs.map((org) => (
                  <li key={org.id}>
                    {/* Row is a div, not a button, so the inner "Manage"
                        button can render without nesting <button> in
                        <button> (which is invalid HTML and triggered React
                        hydration warnings). Keyboard activation is preserved
                        via role + onKeyDown so the row stays focusable. */}
                    <div
                      role="button"
                      tabIndex={0}
                      data-testid={`org-${org.id}`}
                      onClick={() => setSelectedOrg(org.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedOrg(org.id);
                        }
                      }}
                      className={`w-full text-left hover:bg-gray-50 cursor-pointer px-4 py-4 sm:px-6 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-inset ${
                        selectedOrg === org.id ? 'bg-primary-50 border-r-4 border-primary-500' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <BuildingLibraryIcon className="h-8 w-8 text-gray-400 flex-shrink-0" />
                          <div className="ml-4">
                            <div className="text-lg font-medium text-gray-900">{org.name}</div>
                            <div className="flex items-center gap-2 mt-1">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(
                                  org.subscriptionStatus
                                )}`}
                              >
                                {org.subscriptionStatus}
                              </span>
                              {org.subscriptionPlan && (
                                <span className="text-xs text-gray-500">
                                  {org.subscriptionPlan}
                                </span>
                              )}
                              {org.locationCount !== undefined && (
                                <span className="text-xs text-gray-500 flex items-center">
                                  <MapPinIcon className="h-3 w-3 mr-0.5" />
                                  {org.locationCount} location{org.locationCount !== 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            data-testid={`enter-org-${org.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              enterOrg(org.id, org.name);
                            }}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-primary-600 bg-primary-50 rounded-md hover:bg-primary-100 transition-colors"
                          >
                            Manage
                            <ChevronRightIcon className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {orgs.length === 0 && (
              <div className="bg-white shadow rounded-lg text-center py-12">
                <BuildingLibraryIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No organizations</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Get started by creating a new organization.
                </p>
                <div className="mt-6">
                  <button
                    type="button"
                    onClick={() => setShowAddOrgModal(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                  >
                    <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                    Add Organization
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-6">
            {selectedOrgData ? (
              <>
                <div className="bg-white shadow rounded-lg" data-testid="org-detail">
                  <div className="px-4 py-5 sm:p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Organization Details
                      </h3>
                      {isSuperAdmin && (
                        <button
                          data-testid={`edit-org-btn-${selectedOrgData.id}`}
                          onClick={() => setEditingOrg(selectedOrgData)}
                          className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
                          title="Edit organization"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-lg font-medium text-gray-900">
                          {selectedOrgData.name}
                        </h4>
                        {selectedOrgData.description && (
                          <p className="text-sm text-gray-500 mt-1">
                            {selectedOrgData.description}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(
                              selectedOrgData.subscriptionStatus
                            )}`}
                          >
                            {selectedOrgData.subscriptionStatus}
                          </span>
                          {selectedOrgData.subscriptionPlan && (
                            <span className="text-xs text-gray-500">
                              Plan: {selectedOrgData.subscriptionPlan}
                            </span>
                          )}
                        </div>
                      </div>
                      <div>
                        <h5 className="text-sm font-medium text-gray-700 flex items-center">
                          <EnvelopeIcon className="h-4 w-4 mr-2" />
                          Admin Email
                        </h5>
                        <p className="mt-1 text-sm">
                          <a
                            href={`mailto:${selectedOrgData.adminEmail}`}
                            className="text-primary-600 hover:text-primary-500"
                          >
                            {selectedOrgData.adminEmail}
                          </a>
                        </p>
                      </div>
                      <div>
                        <h5 className="text-sm font-medium text-gray-700 flex items-center">
                          <EnvelopeIcon className="h-4 w-4 mr-2" />
                          Billing Email
                        </h5>
                        <p className="mt-1 text-sm text-gray-900">{selectedOrgData.billingEmail}</p>
                      </div>
                      {selectedOrgData.phone && (
                        <div>
                          <h5 className="text-sm font-medium text-gray-700 flex items-center">
                            <PhoneIcon className="h-4 w-4 mr-2" />
                            Phone
                          </h5>
                          <p className="mt-1 text-sm">
                            <a
                              href={`tel:${selectedOrgData.phone}`}
                              className="text-primary-600 hover:text-primary-500"
                            >
                              {selectedOrgData.phone}
                            </a>
                          </p>
                        </div>
                      )}
                      {selectedOrgData.website && (
                        <div>
                          <h5 className="text-sm font-medium text-gray-700 flex items-center">
                            <GlobeAltIcon className="h-4 w-4 mr-2" />
                            Website
                          </h5>
                          <p className="mt-1 text-sm">
                            <a
                              href={selectedOrgData.website}
                              target="_blank"
                              rel="noreferrer"
                              className="text-primary-600 hover:text-primary-500"
                            >
                              {selectedOrgData.website}
                            </a>
                          </p>
                        </div>
                      )}
                      <div className="pt-4 border-t border-gray-200">
                        <p className="text-xs text-gray-500">
                          Created {formatDublinDate(selectedOrgData.createdAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6 text-center">
                  <BuildingLibraryIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Select an organization</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Choose an organization to view details and locations.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {showAddOrgModal && <AddOrgModal onClose={() => setShowAddOrgModal(false)} />}
        {editingOrg && <EditOrgModal org={editingOrg} onClose={() => setEditingOrg(null)} />}
      </div>
    </div>
  );
};

/** Add Organization Modal */
const AddOrgModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [createOrg, { isLoading }] = useCreateOrganizationMutation();
  const [form, setForm] = useState({
    name: '',
    adminEmail: '',
    subscriptionPlan: 'basic',
    website: '',
    phone: '',
    description: '',
  });
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.name || !form.adminEmail) {
      setError('Organization name and admin email are required');
      return;
    }
    try {
      await createOrg({
        name: form.name,
        adminEmail: form.adminEmail,
        subscriptionPlan: form.subscriptionPlan || 'basic',
        website: form.website || undefined,
        phone: form.phone || undefined,
        description: form.description || undefined,
      } as any).unwrap();
      onClose();
    } catch (err: any) {
      setError(err.data?.error || 'Failed to create organization');
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" data-testid="add-org-modal">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />
        <div className="relative z-10 bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl w-full max-w-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Add Organization</h3>
          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Organization Name *</label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Admin Email *</label>
              <input
                name="adminEmail"
                type="email"
                value={form.adminEmail}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">
                This person will be the org admin. If they don't have an account, one will be
                created.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Plan</label>
                <select
                  name="subscriptionPlan"
                  value={form.subscriptionPlan}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  <option value="basic">Basic</option>
                  <option value="premium">Premium</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Website</label>
              <input
                name="website"
                value={form.website}
                onChange={handleChange}
                placeholder="https://"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                rows={2}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                {isLoading ? 'Creating...' : 'Add Organization'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

/** Edit Organization Modal */
const EditOrgModal: React.FC<{ org: OrganizationData; onClose: () => void }> = ({
  org,
  onClose,
}) => {
  const [updateOrg, { isLoading }] = useUpdateOrganizationMutation();
  const [form, setForm] = useState({
    name: org.name || '',
    adminEmail: org.adminEmail || '',
    subscriptionPlan: org.subscriptionPlan || 'basic',
    website: org.website || '',
    phone: org.phone || '',
    description: org.description || '',
  });
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.name || !form.adminEmail) {
      setError('Name and admin email are required');
      return;
    }
    try {
      await updateOrg({
        organizationId: org.id,
        updates: {
          name: form.name,
          adminEmail: form.adminEmail,
          subscriptionPlan: form.subscriptionPlan || undefined,
          website: form.website || undefined,
          phone: form.phone || undefined,
          description: form.description || undefined,
        },
      }).unwrap();
      onClose();
    } catch (err: any) {
      setError(err.data?.error || 'Failed to update organization');
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" data-testid="edit-org-modal">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />
        <div className="relative z-10 bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl w-full max-w-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Organization</h3>
          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Organization Name *</label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Admin Email *</label>
              <input
                name="adminEmail"
                type="email"
                value={form.adminEmail}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Plan</label>
                <select
                  name="subscriptionPlan"
                  value={form.subscriptionPlan}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  <option value="basic">Basic</option>
                  <option value="premium">Premium</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Website</label>
              <input
                name="website"
                value={form.website}
                onChange={handleChange}
                placeholder="https://"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                rows={2}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Organizations;
